import { cs } from '../../../lib/currency';
import {
  Calendar, Clock, Check, X, AlertCircle, GripVertical, RefreshCw,
  Trash2, CalendarClock, CreditCard, Loader2,
} from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { toast } from 'sonner';
import api from '../../../services/api';
import PaymentMethodChooser from '../../checkout/PaymentMethodChooser';
import { formatDate, formatTime } from './utils';

/**
 * "My Lane Bookings" card — three-tab view (Upcoming / Payment Due / Past)
 * with drag-to-reschedule on the Upcoming tab, "Pay Now" on the Payment Due
 * tab (calls `/lanes/bookings/{id}/create-payment-link`), and read-only
 * history on the Past tab.
 *
 * Pure presentation: parent owns `myBookings` and the drag/cancel/reschedule
 * handlers. We only own the small `Pay Now` flow because it doesn't share
 * state with anything else.
 */
const MyBookingsTab = ({
  myBookings,
  lanes,
  isAdmin,
  upcomingBookings,
  pendingPaymentBookings,
  pastBookings,
  canReschedule,
  onCancelBooking,
  onOpenReschedule,
  onOpenEditSeries,
  onDragStart,
  onDragEnd,
  onRefresh,
}) => {
  // Two-step pay-now: clicking "Pay Now" opens a chooser dialog, the
  // actual API call only fires after the parent picks Card or ACH.
  // (Hooks must come BEFORE any early return.)
  const [payBooking, setPayBooking] = useState(null);   // booking being paid
  const [payChoice, setPayChoice] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  if (!myBookings || myBookings.length === 0) return null;

  const handlePayNow = (e, booking) => {
    e.stopPropagation();
    setPayChoice(null);
    setPayBooking(booking);
  };

  const handleConfirmPay = async () => {
    if (!payBooking || !payChoice) return;
    setPayLoading(true);
    try {
      toast.info('Preparing secure checkout...');
      const res = await api.post(
        `/lanes/bookings/${payBooking.id}/create-payment-link`,
        { payment_method_choice: payChoice },
      );
      const data = res.data || {};
      if (data.requires_payment === false) {
        toast.success(data.message || 'Booking paid using account credit');
        setPayBooking(null);
        onRefresh?.();
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error('Could not start payment. Please contact the academy.');
      setPayLoading(false);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        'Could not start payment. Please contact the academy.';
      toast.error(detail);
      setPayLoading(false);
    }
  };

  return (
    <Card className="border border-slate-200">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            My Lane Bookings
          </CardTitle>
          {pendingPaymentBookings.length > 0 && (
            <Badge className="bg-amber-100 text-amber-700">
              {pendingPaymentBookings.length} payment{pendingPaymentBookings.length > 1 ? 's' : ''} pending
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <Tabs defaultValue="upcoming" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="upcoming" className="text-sm">
              Upcoming ({upcomingBookings.length})
            </TabsTrigger>
            <TabsTrigger value="pending" className="text-sm">
              Payment Due ({pendingPaymentBookings.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="text-sm">
              Past ({pastBookings.length})
            </TabsTrigger>
          </TabsList>

          {/* Upcoming Bookings Tab */}
          <TabsContent value="upcoming" className="mt-0">
            {upcomingBookings.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No upcoming bookings</p>
            ) : (
              <div className="divide-y divide-slate-100 border rounded-lg">
                {upcomingBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className={`p-4 flex items-center justify-between transition-all select-none ${
                      canReschedule(booking)
                        ? 'cursor-grab active:cursor-grabbing hover:bg-slate-50'
                        : 'hover:bg-slate-50'
                    }`}
                    draggable={canReschedule(booking) ? 'true' : 'false'}
                    onDragStart={(e) => onDragStart(e, booking)}
                    onDragEnd={onDragEnd}
                    data-testid={`booking-${booking.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {canReschedule(booking) && (
                        <GripVertical className="h-5 w-5 text-slate-400 flex-shrink-0" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">{booking.lane_name}</p>
                        <p className="text-sm text-slate-600">
                          {formatDate(booking.date)} • {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                        </p>
                        <p className="text-sm text-slate-500">
                          {booking.duration} min
                          {booking.rescheduled_at && (
                            <span className="text-blue-500 ml-2">(Rescheduled)</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">{cs()}{booking.price}</p>
                        {booking.payment_status === 'paid' ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <Check className="h-3 w-3 mr-1" /> Paid
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            <Clock className="h-3 w-3 mr-1" /> Payment Due
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        {isAdmin && booking.recurrence_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-purple-600 hover:bg-purple-50 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenEditSeries({
                                ...booking,
                                lane_name: lanes.find((l) => l.id === booking.lane_id)?.name,
                              });
                            }}
                            data-testid={`edit-series-btn-${booking.id}`}
                          >
                            <CalendarClock className="h-3 w-3 mr-1" />
                            Edit Series
                          </Button>
                        )}
                        {canReschedule(booking) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-blue-600 hover:bg-blue-50 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenReschedule(booking);
                            }}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Reschedule
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCancelBooking(booking.id);
                          }}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Payment Due Tab */}
          <TabsContent value="pending" className="mt-0">
            {pendingPaymentBookings.length === 0 ? (
              <p className="text-slate-500 text-center py-8">All payments complete!</p>
            ) : (
              <div className="divide-y divide-slate-100 border rounded-lg">
                {pendingPaymentBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="p-4 flex items-center justify-between hover:bg-amber-50 transition-colors"
                    data-testid={`pending-booking-${booking.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{booking.lane_name}</p>
                      <p className="text-sm text-slate-600">
                        {formatDate(booking.date)} • {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                      </p>
                      <p className="text-sm text-slate-500">{booking.duration} min</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">{cs()}{booking.price}</p>
                        <Badge className="bg-amber-100 text-amber-700 text-xs">
                          <AlertCircle className="h-3 w-3 mr-1" /> Payment Required
                        </Badge>
                      </div>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`pay-now-btn-${booking.id}`}
                        onClick={(e) => handlePayNow(e, booking)}
                      >
                        <CreditCard className="h-3 w-3 mr-1" />
                        Pay Now
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Past Bookings Tab */}
          <TabsContent value="past" className="mt-0">
            {pastBookings.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No past bookings</p>
            ) : (
              <div className="divide-y divide-slate-100 border rounded-lg max-h-96 overflow-y-auto">
                {pastBookings.slice(0, 20).map((booking) => (
                  <div
                    key={booking.id}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    data-testid={`past-booking-${booking.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{booking.lane_name}</p>
                      <p className="text-sm text-slate-600">
                        {formatDate(booking.date)} • {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                      </p>
                      <p className="text-sm text-slate-500">{booking.duration} min</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">{cs()}{booking.price}</p>
                      {booking.status === 'cancelled' ? (
                        <Badge className="bg-red-100 text-red-700 text-xs">
                          <X className="h-3 w-3 mr-1" /> Cancelled
                        </Badge>
                      ) : booking.payment_status === 'paid' ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <Check className="h-3 w-3 mr-1" /> Paid
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 text-xs">Unpaid</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Pay-now confirm — Card vs ACH chooser */}
      <Dialog
        open={!!payBooking}
        onOpenChange={(o) => { if (!o && !payLoading) { setPayBooking(null); } }}
      >
        <DialogContent className="max-w-md" data-testid="lane-pay-now-dialog">
          <DialogHeader>
            <DialogTitle>Pay for your booking</DialogTitle>
            <DialogDescription>
              {payBooking?.lane_name || 'Lane booking'}
              {payBooking?.date ? ` · ${formatDate(payBooking.date)}` : ''}
              {payBooking?.start_time ? ` · ${formatTime(payBooking.start_time)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <PaymentMethodChooser
            baseAmount={Number(payBooking?.amount_due ?? payBooking?.total_price ?? payBooking?.price) || 0}
            value={payChoice}
            onChange={setPayChoice}
            disabled={payLoading}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayBooking(null)} disabled={payLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPay}
              disabled={!payChoice || payLoading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="lane-pay-now-confirm-btn"
            >
              {payLoading
                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Redirecting…</>
                : 'Continue to Checkout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MyBookingsTab;
